import { FieldResponseSubscriptions } from "./FieldResponseSubscriptions";
import Dexie, { type EntityTable } from "dexie";
import type { InfoStoreReader, SideEffect } from ".";
import { getFieldResponsesId } from "../aggregate";
import { resolveIntentEffects } from "./resolveIntents";

type Db = Dexie & {
  fieldResponses: EntityTable<
    {
      proposalId: string;
      isCurrent: boolean;
      fieldResponsesId: string;
      fieldResponses: Record<string, string>;
    },
    "fieldResponsesId"
  >;
};

function getDb(): Db {
  const db = new Dexie("ClientInfoStore") as Db;

  db.version(1).stores({
    fieldResponses:
      "fieldResponsesId, [proposalId+fieldResponsesId], proposalId",
  });

  return db;
}

/**
 * Dev tooling (SP-2637): drop every persisted field-response row, from
 * outside any store instance. The rows are a refetchable cache of proposal
 * text with no per-round key, so the "Forget this round on this device" wipe
 * clears the whole table; the next round load re-fetches what it needs.
 */
export async function clearPersistedFieldResponses(): Promise<void> {
  const db = getDb();

  try {
    await db.fieldResponses.clear();
  } finally {
    db.close();
  }
}

export default class ClientInfoStore implements InfoStoreReader {
  db: Db;

  // In-flight cold-client warmer (see prefetchAllCurrentIfCold). Never
  // rejects — failures resolve to {} and the on-demand fetch covers the rest.
  #prefetchedAll: Promise<Record<string, Record<string, string>>> | null = null;

  // True once the cold check observed an EMPTY fieldResponses table and
  // nothing has been written since. Lets ensureFieldResponses skip a bulkGet
  // that can only miss — on the iPad's Lockdown-Mode storage stand-in, a
  // few hundred guaranteed-miss reads cost whole seconds of a round load.
  // Cleared before any write, so a later ensure sees real cache contents.
  #dbKnownEmpty = false;

  // Map<proposalId, Map<version, fieldResponses>>
  #cache: Map<string, Map<string, Record<string, string>>> = new Map();

  // Map<proposalId, latestVersion>
  #latestVersion: Map<string, string> = new Map();

  // A late fetch must not restore a version purged while it was in flight.
  #deletedProposals = new Set<string>();

  #subscriptions = new FieldResponseSubscriptions();

  constructor(
    private fetchFieldResponses?: (
      fieldResponsesIds: string[],
    ) => Promise<Record<string, Record<string, string>>>,
    db?: Db,
    // Optional offline signal (passed in so the engine stays decoupled from the
    // client network hook). Receives the fetch error so the caller can also
    // tolerate transport-level failures (backend unreachable, SP-2612). When it
    // reports offline/unreachable, a failed field-response fetch is tolerated
    // (serve cached); otherwise the failure is rethrown.
    private isOffline?: (error?: unknown) => boolean,
  ) {
    this.db = db ?? getDb();
  }

  /**
   * Cold-client warmer. On a browser with nothing persisted yet (first visit,
   * new device, cleared storage) the round load's ensureFieldResponses ends in
   * one big network fetch — late, because the version ids it needs come from
   * the aggregate, which itself takes most of the load to arrive. But the
   * SERVER can derive the current ids from the round alone, so a cold client
   * may start that fetch immediately (`fetchAll` = getFieldResponses with no
   * ids). ensureFieldResponses consumes the result and only network-fetches
   * versions it didn't cover. On a warm client this is a no-op after one
   * indexed count, so returning users never re-download proposal text they
   * already have.
   */
  async prefetchAllCurrentIfCold(
    fetchAll: () => Promise<Record<string, Record<string, string>>>,
  ): Promise<void> {
    if (this.#prefetchedAll != null) {
      return;
    }

    try {
      if ((await this.db.fieldResponses.count()) > 0) {
        return;
      }

      this.#dbKnownEmpty = true;
    } catch {
      return; // unreadable local db — let the normal load path handle it
    }

    this.#prefetchedAll = fetchAll().catch((e: unknown) => {
      console.error(
        "field-response prefetch failed; falling back to the on-demand fetch",
        e,
      );
      return {};
    });
  }

  readNewestFieldResponses(proposalId: string): Record<string, string> | null {
    const version = this.#latestVersion.get(proposalId);

    if (!version) {
      return null;
    }

    return this.#cache.get(proposalId)?.get(version) ?? null;
  }

  readFieldResponsesFromCache(
    proposalId: string,
    version: string,
  ): Record<string, string> | null {
    return this.#cache.get(proposalId)?.get(version) ?? null;
  }

  getAllFieldResponses(): Record<string, Record<string, string>> {
    const result: Record<string, Record<string, string>> = {};

    for (const [proposalId, version] of this.#latestVersion.entries()) {
      const responses = this.#cache.get(proposalId)?.get(version);

      if (responses) {
        result[proposalId] = responses;
      }
    }

    return result;
  }

  async readFieldResponsesAtVersion(
    proposalId: string,
    version: string,
  ): Promise<Record<string, string> | null> {
    const probed = await this.probeFieldResponsesAtVersion(proposalId, version);
    return probed.status === "found" ? probed.fieldResponses : null;
  }

  /**
   * Like readFieldResponsesAtVersion, but tells a null apart: "absent" means
   * the server authoritatively lacks this version (it will never turn up, so
   * a caller must not wait on it), "unreachable" means it could not be
   * looked up right now (offline, fetch failure) and a retry can succeed.
   */
  async probeFieldResponsesAtVersion(
    proposalId: string,
    version: string,
  ): Promise<
    | { status: "found"; fieldResponses: Record<string, string> }
    | { status: "absent" }
    | { status: "unreachable" }
  > {
    if (this.#deletedProposals.has(proposalId)) {
      return version === this.#latestVersion.get(proposalId)
        ? { status: "found", fieldResponses: {} }
        : { status: "absent" };
    }

    // 1. Check Memory
    const cached = this.#cache.get(proposalId)?.get(version);

    if (cached) {
      return { status: "found", fieldResponses: cached };
    }

    // 2. Check DB. A failed cache read is a cache miss, not a load failure:
    // fall through to the fetch rather than report the version as absent
    // over a broken IndexedDB (same policy as ensureFieldResponses).
    let fieldResponses: { fieldResponses: Record<string, string> } | undefined;

    try {
      fieldResponses = await this.db.fieldResponses.get({
        fieldResponsesId: version,
      });
    } catch (e) {
      console.error(
        `Failed to read field responses for version ${version} from cache; trying the server`,
        e,
      );
    }

    if (this.#deletedProposals.has(proposalId)) {
      return { status: "absent" };
    }

    if (fieldResponses) {
      this.#addToCache(proposalId, version, fieldResponses.fieldResponses);
      return { status: "found", fieldResponses: fieldResponses.fieldResponses };
    }

    // 3. Fetch — skipped while offline: an offline round open must not
    // block on the network. The version is retried next online load.
    if (this.fetchFieldResponses && !(this.isOffline?.() ?? false)) {
      try {
        const fetched = await this.fetchFieldResponses([version]);

        if (this.#deletedProposals.has(proposalId) || !fetched[version]) {
          return { status: "absent" };
        }

        // Cache write only — best-effort, so a broken IndexedDB doesn't
        // discard text we just fetched successfully.
        try {
          this.#dbKnownEmpty = false;
          await this.db.fieldResponses.put({
            proposalId,
            isCurrent: false,
            fieldResponsesId: version,
            fieldResponses: fetched[version],
          });
        } catch (e) {
          console.error(
            `Failed to cache field responses for version ${version}; serving fetched copy`,
            e,
          );
        }

        if (this.#deletedProposals.has(proposalId)) {
          return { status: "absent" };
        }

        this.#addToCache(proposalId, version, fetched[version]);
        return { status: "found", fieldResponses: fetched[version] };
      } catch (e) {
        console.error(
          `Failed to fetch field responses for version ${version}`,
          e,
        );
        return { status: "unreachable" };
      }
    }

    return { status: "unreachable" };
  }

  #addToCache(
    proposalId: string,
    version: string,
    fieldResponses: Record<string, string>,
  ) {
    if (!this.#cache.has(proposalId)) {
      this.#cache.set(proposalId, new Map());
    }

    this.#cache.get(proposalId)!.set(version, fieldResponses);
  }

  subscribeToFieldResponses(
    proposalId: string,
    callback: (fieldResponses: Record<string, string>) => void,
  ): () => void {
    return this.#subscriptions.subscribeToFieldResponses(proposalId, callback);
  }

  subscribeToAllFieldResponses(
    callback: (fieldResponses: Record<string, Record<string, string>>) => void,
  ): () => void {
    return this.#subscriptions.subscribeToAllFieldResponses(callback);
  }

  async applySideEffects(rawSideEffects: SideEffect[]): Promise<void> {
    const changedProposalIds = new Set<string>();
    // Resolve the reducer's intents against this store's cache first.
    const sideEffects = resolveIntentEffects(rawSideEffects, {
      byVersion: (proposalId, version) =>
        this.readFieldResponsesFromCache(proposalId, version),
      newest: (proposalId) => this.readNewestFieldResponses(proposalId),
    });

    for (const sideEffect of sideEffects) {
      if (sideEffect.type === "PURGE_APPLICATION_FIELD_RESPONSES") {
        const { proposalId, fieldResponsesId } = sideEffect;
        this.#deletedProposals.add(proposalId);
        this.#cache.set(proposalId, new Map([[fieldResponsesId, {}]]));
        this.#latestVersion.set(proposalId, fieldResponsesId);
        changedProposalIds.add(proposalId);
        await this.db.fieldResponses
          .where("proposalId")
          .equals(proposalId)
          .delete();
      } else if (sideEffect.type === "WRITE_FIELD_RESPONSES") {
        if (this.#deletedProposals.has(sideEffect.proposalId)) {
          continue;
        }

        const version = getFieldResponsesId(sideEffect.fieldResponses);
        this.#addToCache(
          sideEffect.proposalId,
          version,
          sideEffect.fieldResponses,
        );
        this.#latestVersion.set(sideEffect.proposalId, version);
        changedProposalIds.add(sideEffect.proposalId);
      }
      // Ignore other (round / round member) side effects
    }

    // Batch Notify
    if (changedProposalIds.size > 0) {
      this.#batchNotifySubscribers(changedProposalIds);
    }

    this.#dbKnownEmpty = false;
    await this.db.fieldResponses.bulkPut(
      Array.from(changedProposalIds).map((proposalId) => {
        const version = this.#latestVersion.get(proposalId)!;
        const fieldResponses = this.#cache.get(proposalId)!.get(version)!;
        return {
          proposalId,
          isCurrent: true,
          fieldResponsesId: version,
          fieldResponses,
        };
      }),
    );
  }

  /**
   * Drop every cached field response, in memory and on disk. Called on an
   * account/schema change so the previous account's proposal text does not
   * linger next to a freshly-wiped round store (under Lockdown Mode that text
   * is persisted to a native file). Field responses are re-fetched on demand,
   * so this is always safe.
   */
  async clearFieldResponseCache(): Promise<void> {
    this.#deletedProposals.clear();
    this.#cache.clear();
    this.#latestVersion.clear();
    await this.db.fieldResponses.clear();
  }

  #batchNotifySubscribers(changedProposalIds: Set<string>) {
    this.#subscriptions.notify(
      changedProposalIds,
      (proposalId) => this.readNewestFieldResponses(proposalId),
      () => this.getAllFieldResponses(),
    );
  }

  async ensureFieldResponses(
    requiredVersions: { proposalId: string; version: string }[],
  ): Promise<void> {
    const missingFromMemory = requiredVersions.filter(
      (rv) =>
        !this.#deletedProposals.has(rv.proposalId) &&
        !this.#cache.get(rv.proposalId)?.has(rv.version),
    );

    if (missingFromMemory.length === 0) {
      return;
    }

    const versions = missingFromMemory.map((rv) => rv.version);
    let dbItems: ({ fieldResponses: Record<string, string> } | undefined)[];

    if (this.#dbKnownEmpty) {
      // Every keyed get would miss; skip straight to the fetch below.
      dbItems = versions.map(() => undefined);
    } else {
      // A failed cache read is a cache miss, not a load failure: fall through
      // to the network fetch below rather than failing the round load over a
      // broken IndexedDB (backend lost / corrupt profile — both seen in
      // production).
      try {
        dbItems = await this.db.fieldResponses.bulkGet(versions);
      } catch (e) {
        console.error(
          `Failed to read ${versions.length} field-response version(s) from cache; fetching from server`,
          e,
        );
        dbItems = versions.map(() => undefined);
      }
    }

    const missingFromDb: { proposalId: string; version: string }[] = [];
    const changedProposalIds = new Set<string>();

    for (let i = 0; i < missingFromMemory.length; i++) {
      const rv = missingFromMemory[i];
      const dbItem = dbItems[i];

      if (rv == null || this.#deletedProposals.has(rv.proposalId)) {
        continue;
      }

      if (dbItem) {
        // Found in DB
        this.#addToCache(rv.proposalId, rv.version, dbItem.fieldResponses);
        this.#latestVersion.set(rv.proposalId, rv.version);
        changedProposalIds.add(rv.proposalId);
      } else {
        missingFromDb.push(rv);
      }
    }

    if (missingFromDb.length > 0) {
      if (!this.fetchFieldResponses) {
        console.warn(
          "fetchFieldResponses not provided, cannot fetch missing field responses",
        );
      } else {
        // Fetch missing from server
        const uniqueVersions = [
          ...new Set(missingFromDb.map((m) => m.version)),
        ];

        if (uniqueVersions.length > 0) {
          // A cold-client prefetch (prefetchAllCurrentIfCold) may already hold
          // most of these versions — consume it first and network-fetch only
          // the remainder. Bounded wait: if the prefetch is unexpectedly slow,
          // fall through to the normal fetch rather than gate the round load
          // on it (the prefetch settling later is harmless).
          const fromPrefetch: Record<string, Record<string, string>> = {};
          let versionsToFetch = uniqueVersions;

          if (this.#prefetchedAll != null) {
            const prefetched = await Promise.race([
              this.#prefetchedAll,
              new Promise<Record<string, Record<string, string>>>((resolve) =>
                setTimeout(() => resolve({}), 3_000),
              ),
            ]);
            versionsToFetch = uniqueVersions.filter((version) => {
              const hit = prefetched[version];

              if (hit) {
                fromPrefetch[version] = hit;
              }

              return hit == null;
            });
          }

          // A fetch failure while OFFLINE must not abort the whole round load:
          // serve whatever is already in memory/Dexie and leave the unfetchable
          // versions absent (the UI renders a placeholder). But an ONLINE
          // failure (server/permission/serialization error) is a real fault —
          // swallowing it would let funders evaluate applications with proposal
          // text silently missing, so rethrow it. When no offline signal is
          // wired (desktop/tests), treat as online and rethrow (original behavior).
          let fetched: Record<string, Record<string, string>> = fromPrefetch;

          try {
            if (versionsToFetch.length > 0) {
              fetched = {
                ...fromPrefetch,
                ...(await this.fetchFieldResponses(versionsToFetch)),
              };
            }
          } catch (e) {
            if (!(this.isOffline?.(e) ?? false)) {
              throw e;
            }

            console.error(
              `Failed to fetch ${versionsToFetch.length} field-response version(s) while offline; serving cached only`,
              e,
            );
          }

          for (const [version, fieldResponses] of Object.entries(fetched)) {
            const proposals = missingFromDb.filter(
              (m) => m.version === version,
            );

            for (const p of proposals) {
              if (this.#deletedProposals.has(p.proposalId)) {
                continue;
              }

              this.#addToCache(p.proposalId, version, fieldResponses);
              this.#latestVersion.set(p.proposalId, version);
              changedProposalIds.add(p.proposalId);
            }
          }

          // Persisting the fetched text is a cache write — the in-memory
          // copies above are what render, so the round load does not wait on
          // it (a few hundred rows on the iPad's Lockdown-Mode stand-in cost
          // ~1.7s). Best-effort for the same reason: a full disk or dead
          // IndexedDB backend cannot fail a round load whose data is in hand.
          // Ordered before ensure resolves: Dexie serializes same-table
          // transactions, so the round-load gc that follows still runs after
          // this write.
          this.#dbKnownEmpty = false;
          void this.db.fieldResponses
            .bulkPut(
              missingFromDb
                .map(({ proposalId, version }) => {
                  const fieldResponses = fetched[version];

                  if (
                    this.#deletedProposals.has(proposalId) ||
                    !fieldResponses
                  ) {
                    return null;
                  }

                  return {
                    proposalId,
                    isCurrent: true,
                    fieldResponsesId: version,
                    fieldResponses,
                  };
                })
                .filter((m) => m !== null),
            )
            .catch((e: unknown) => {
              console.error(
                "Failed to persist fetched field responses to cache; continuing with in-memory copies",
                e,
              );
            });
        }
      }
    }

    if (changedProposalIds.size > 0) {
      this.#batchNotifySubscribers(changedProposalIds);
    }
  }

  async gc(keepFieldResponsesIds: Set<string>): Promise<void> {
    // A version some proposal currently points at in THIS store must survive
    // even when the caller's keep-set doesn't name it: on deferred round loads
    // (see Round.tsx) gc runs after the round is live, so a
    // WRITE_FIELD_RESPONSES — a local edit or one arriving over the WebSocket
    // — can create a version newer than the caller's load-time snapshot.
    // Deleting it would drop the proposal's current text from memory and
    // IndexedDB with nothing left to re-fetch it (the consumer hooks only read
    // and subscribe). #latestVersion is written synchronously before any async
    // persistence, and nothing awaits between this union and the memory sweep,
    // so the only write the union can miss is one landing after the IndexedDB
    // sweep below has started — whose in-memory copy survives regardless, so
    // at worst the next load re-fetches that version.
    const keep = new Set([
      ...keepFieldResponsesIds,
      ...this.#latestVersion.values(),
    ]);

    // Clean memory
    for (const [proposalId, versionMap] of this.#cache.entries()) {
      for (const version of versionMap.keys()) {
        if (!keep.has(version)) {
          versionMap.delete(version);
        }
      }

      if (versionMap.size === 0) {
        this.#cache.delete(proposalId);
        this.#latestVersion.delete(proposalId);
      } else {
        // Unreachable now that `keep` unions in every latest version; retained
        // as a safety net so a future keep-set change can't leave
        // #latestVersion pointing at an evicted entry.
        const currentLatest = this.#latestVersion.get(proposalId);

        if (currentLatest && !keep.has(currentLatest)) {
          this.#latestVersion.delete(proposalId);
        }
      }
    }

    // Cache housekeeping only — best-effort, so a broken IndexedDB can't fail
    // the round load that runs this (unswept entries are retried next load).
    try {
      await this.db.fieldResponses
        .filter((item) => !keep.has(item.fieldResponsesId))
        .delete();
    } catch (e) {
      console.error("Failed to gc field-response cache; continuing", e);
    }
  }
}
